import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  updateTreatmentPlanUrl,
  getPatientActiveTreatmentPlanUrl,
  deleteTreatmentPlanActionUrl,
  completeConsultationHistoryUrl,
  generateAITreatmentPlanUrl,
  linkAppointmentConsultationUrl,
  updateTreatmentPlanFollowUpStatus,
  addConsultationLinkedTemplateUrl,
} from "../../apiUrls/apiUrls";
import {
  httpGet,
  httpPost,
} from "../../services/httpService";

const initialState = {
  treatmentPlanDrafts: {},
  activeConsultationId: null,
  lastSavedAt: null,
  isUpdatingTreatmentPlan: false,
  updateTreatmentPlanError: null,
  updateTreatmentPlanSuccess: false,
  lastUpdatedTreatmentPlan: null,
  patientActiveTreatmentPlans: [],
  activeTreatmentPlansLoader: false,
  isLinkTreatmentPlanDialogOpen: false,
  isDeletingTreatmentPlanAction: false,
  deleteTreatmentPlanActionError: null,
  deleteTreatmentPlanActionSuccess: false,
  linkedTreatmentPlanId: null,
  linkedTreatmentPlanAppointmentId: null,
  isAITreatmentPlanLoading: false,
  isLinkingAppointmentConsultation: false,
  linkAppointmentConsultationError: null,
};

export const UpdateTreatmentPlanData = createAsyncThunk(
  "treatmentPlan/UpdateTreatmentPlanData",
  async (data, thunkApi) => {
    try {
      const url = updateTreatmentPlanUrl();
      const respData = await httpPost(url, data);
      return respData;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to update treatment plan",
      );
    }
  },
);

export const getPatientActiveTreatmentPlanData = createAsyncThunk(
  "treatmentPlan/getPatientActiveTreatmentPlanData",
  async (patientId, thunkApi) => {
    const urlToGet = getPatientActiveTreatmentPlanUrl(patientId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const deleteTreatmentPlanActionData = createAsyncThunk(
  "treatmentPlan/deleteTreatmentPlanActionData",
  async (data, thunkApi) => {
    try {
      const url = deleteTreatmentPlanActionUrl();
      const respData = await httpPost(url, data);
      return respData;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to delete treatment plan action",
      );
    }
  },
);

export const completeConsultationHistoryData = createAsyncThunk(
  "treatmentPlan/completeConsultationHistoryData",
  async (data, thunkApi) => {
    try {
      const url = completeConsultationHistoryUrl();
      const respData = await httpPost(url, data);
      return respData;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to complete consultation history",
      );
    }
  },
);

export const generateAITreatmentPlanData = createAsyncThunk(
  "treatmentPlan/generateAITreatmentPlanData",
  async (data, thunkApi) => {
    const url = generateAITreatmentPlanUrl();
    const respData = await httpPost(url, data);
    return respData;
  },
);

// ── NEW: Link appointment to consultation via dedicated API ───────────────────
export const linkAppointmentConsultationData = createAsyncThunk(
  "treatmentPlan/linkAppointmentConsultationData",
  async (data, thunkApi) => {
    try {
      const url = linkAppointmentConsultationUrl();
      const respData = await httpPost(url, data);
      return respData;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to link appointment consultation",
      );
    }
  },
);

export const updateTreatmentPlanFollowUpStatusData = createAsyncThunk(
  "treatmentPlan/updateTreatmentPlanFollowUpStatusData",
  async (data, thunkApi) => {
    try {
      const url = updateTreatmentPlanFollowUpStatus();
      const respData = await httpPost(url, data);
      return respData;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to link appointment consultation",
      );
    }
  },
);

export const addConsultationLinkedTemplateData = createAsyncThunk(
  "treatmentPlan/addConsultationLinkedTemplateData",
  async (data, thunkApi) => {
    try {
      const url = addConsultationLinkedTemplateUrl();
      const respData = await httpPost(url, data);
      return respData;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to save consultation linked template",
      );
    }
  },
);

const treatmentPlanSlice = createSlice({
  name: "treatmentPlan",
  initialState,
  reducers: {
    saveTreatmentPlanDraft: (state, action) => {
      const { consultationId, draftData } = action.payload;
      if (consultationId) {
        state.treatmentPlanDrafts[consultationId] = {
          data: draftData,
          savedAt: new Date().toISOString(),
        };
        state.lastSavedAt = new Date().toISOString();
      }
    },

    getTreatmentPlanDraft: (state, action) => {
      const { consultationId } = action.payload;
      return state.treatmentPlanDrafts[consultationId] || null;
    },

    clearTreatmentPlanDraft: (state, action) => {
      const { consultationId } = action.payload;
      if (consultationId && state.treatmentPlanDrafts[consultationId]) {
        delete state.treatmentPlanDrafts[consultationId];
      }
    },

    clearAllTreatmentPlanDrafts: (state) => {
      state.treatmentPlanDrafts = {};
      state.lastSavedAt = null;
    },

    setActiveConsultationId: (state, action) => {
      state.activeConsultationId = action.payload;
    },

    resetUpdateTreatmentPlanState: (state) => {
      state.isUpdatingTreatmentPlan = false;
      state.updateTreatmentPlanError = null;
      state.updateTreatmentPlanSuccess = false;
      state.isDeletingTreatmentPlanAction = false;
      state.deleteTreatmentPlanActionError = null;
      state.deleteTreatmentPlanActionSuccess = false;
    },

    setLinkTreatmentPlanDialogOpen: (state, action) => {
      state.isLinkTreatmentPlanDialogOpen = action.payload;
    },

    setLinkedTreatmentPlanId: (state, action) => {
      if (
        action.payload &&
        typeof action.payload === "object" &&
        !Array.isArray(action.payload)
      ) {
        state.linkedTreatmentPlanId = action.payload.planId || null;
        state.linkedTreatmentPlanAppointmentId =
          action.payload.appointmentId || null;
        return;
      }
      state.linkedTreatmentPlanId = action.payload;
      state.linkedTreatmentPlanAppointmentId = null;
    },

    clearLinkedTreatmentPlanId: (state) => {
      state.linkedTreatmentPlanId = null;
      state.linkedTreatmentPlanAppointmentId = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(UpdateTreatmentPlanData.pending, (state) => {
        state.isUpdatingTreatmentPlan = true;
        state.updateTreatmentPlanError = null;
        state.updateTreatmentPlanSuccess = false;
      })
      .addCase(UpdateTreatmentPlanData.fulfilled, (state, action) => {
        state.isUpdatingTreatmentPlan = false;
        state.updateTreatmentPlanSuccess = true;
        state.lastUpdatedTreatmentPlan =
          action.meta?.arg?.TreatmentPlan || null;
      })
      .addCase(UpdateTreatmentPlanData.rejected, (state, action) => {
        state.isUpdatingTreatmentPlan = false;
        state.updateTreatmentPlanError =
          action.payload || "Failed to update treatment plan";
        state.updateTreatmentPlanSuccess = false;
      })
      .addCase(getPatientActiveTreatmentPlanData.pending, (state) => {
        state.activeTreatmentPlansLoader = true;
      })
      .addCase(getPatientActiveTreatmentPlanData.fulfilled, (state, action) => {
        state.activeTreatmentPlansLoader = false;
        state.patientActiveTreatmentPlans = action?.payload || null;
      })
      .addCase(getPatientActiveTreatmentPlanData.rejected, (state) => {
        state.activeTreatmentPlansLoader = false;
      })
      .addCase(deleteTreatmentPlanActionData.pending, (state) => {
        state.isDeletingTreatmentPlanAction = true;
        state.deleteTreatmentPlanActionError = null;
        state.deleteTreatmentPlanActionSuccess = false;
      })
      .addCase(deleteTreatmentPlanActionData.fulfilled, (state) => {
        state.isDeletingTreatmentPlanAction = false;
        state.deleteTreatmentPlanActionSuccess = true;
      })
      .addCase(deleteTreatmentPlanActionData.rejected, (state, action) => {
        state.isDeletingTreatmentPlanAction = false;
        state.deleteTreatmentPlanActionError =
          action.payload || "Failed to delete treatment plan action";
        state.deleteTreatmentPlanActionSuccess = false;
      })
      .addCase(generateAITreatmentPlanData.pending, (state) => {
        state.isAITreatmentPlanLoading = true;
      })
      .addCase(generateAITreatmentPlanData.fulfilled, (state) => {
        state.isAITreatmentPlanLoading = false;
      })
      .addCase(generateAITreatmentPlanData.rejected, (state) => {
        state.isAITreatmentPlanLoading = false;
      })
      // ── linkAppointmentConsultation lifecycle ─────────────────────────────
      .addCase(linkAppointmentConsultationData.pending, (state) => {
        state.isLinkingAppointmentConsultation = true;
        state.linkAppointmentConsultationError = null;
      })
      .addCase(linkAppointmentConsultationData.fulfilled, (state) => {
        state.isLinkingAppointmentConsultation = false;
      })
      .addCase(linkAppointmentConsultationData.rejected, (state, action) => {
        state.isLinkingAppointmentConsultation = false;
        state.linkAppointmentConsultationError =
          action.payload || "Failed to link appointment consultation";
      });
  },
});

export const {
  saveTreatmentPlanDraft,
  getTreatmentPlanDraft,
  clearTreatmentPlanDraft,
  clearAllTreatmentPlanDrafts,
  setActiveConsultationId,
  resetUpdateTreatmentPlanState,
  setLinkTreatmentPlanDialogOpen,
  setLinkedTreatmentPlanId,
  clearLinkedTreatmentPlanId,
} = treatmentPlanSlice.actions;

export default treatmentPlanSlice.reducer;
